import { Bell } from 'lucide-react';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shared/utils/utils';
import { useNotifications, useNotificationMutations } from '@/features/activity/hooks/useNotifications';

export const notificationsConfig: WidgetConfig = {
  id: 'dash-notifications',
  title: 'Notifications',
  category: 'list',
  description: 'Your latest alerts',
  refreshStrategy: 'disabled', // the hook polls every 60s on its own
  defaultSize: { w: 6, h: 3 },
  priority: 2,
};

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function NotificationsWidget() {
  const { data, isLoading, isError, error } = useNotifications(20);
  const { markRead, markAllRead } = useNotificationMutations();

  const notifications = (data?.notifications ?? []).slice(0, 8);
  const unread = data?.unreadCount ?? 0;

  return (
    <WidgetContainer
      config={notificationsConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      actions={
        <div className="flex items-center gap-2">
          {unread > 0 && <Badge tone="danger">{unread}</Badge>}
          {unread > 0 && (
            <Button size="sm" variant="ghost" loading={markAllRead.isPending} onClick={() => markAllRead.mutate()}>
              Mark all read
            </Button>
          )}
        </div>
      }
    >
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <Bell className="h-6 w-6 text-on-surface-variant" />
          <p className="text-body-sm text-on-surface-variant">You're all caught up.</p>
        </div>
      ) : (
        <ul className="flex flex-col">
          {notifications.map((n) => (
            <li
              key={n.id}
              onClick={() => !n.is_read && markRead.mutate(n.id)}
              className={cn(
                'flex items-start gap-3 border-b border-outline px-1 py-2.5 last:border-0',
                !n.is_read && 'cursor-pointer',
              )}
            >
              <span
                className={cn(
                  'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                  n.is_read ? 'bg-transparent' : 'bg-primary',
                )}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'text-data-md leading-snug',
                    n.is_read ? 'text-on-surface-variant' : 'font-medium text-on-surface',
                  )}
                >
                  {n.message}
                </p>
                <p className="mt-0.5 text-body-sm text-on-surface-variant">{relativeTime(n.created_at)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </WidgetContainer>
  );
}
